import { motion } from "framer-motion";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Badge } from "@/components/ui/badge";


const leaders = [
  {
    name: "Taran Duba",
    role: "Founder & President",
    bio: "Started Byte-Sized Python to make coding approachable for every kid. Manages curriculum development, leads instruction, builds and codes all of Byte-Sized Python's products, and keeps the whole org moving forward.",
    photo: '/taran.webp' as string | null,
    accentColor: "from-primary/20 to-secondary/30",
  },
  {
    name: "Nithin Maheswaran",
    role: "Vice President",
    bio: "Supports the president in day-to-day operations, steps in when needed, and helps coordinate the team to keep everything running smoothly.",
    photo: '/nithin.webp' as string | null,
    accentColor: "from-secondary/20 to-primary/20",
  },
  {
    name: "Vaibhav Lankipalli",
    role: "Secretary",
    bio: "Keeps the organization on track by managing meeting notes, communications, and internal records so nothing falls through the cracks.",
    photo: '/vaibhav.webp' as string | null,
    accentColor: "from-primary/15 to-secondary/25",
  },
  {
    name: "Maitreya Awasthi",
    role: "Treasurer",
    bio: "Handles budgeting, tracks expenses, and manages sponsorship funds to keep the organization financially healthy and our classes accessible.",
    photo: '/maitreya.webp' as string | null,
    accentColor: "from-secondary/25 to-primary/15",
  },
  {
    name: "Bhanu Vanukuri",
    role: "Social Media Manager",
    bio: "Tells our story online — managing posts, designing content, and growing our community so more students and families can discover what we do.",
    photo: '/bhanu.webp' as string | null,
    accentColor: "from-secondary/20 to-primary/25",
  },
  {
    name: "Vishal Duba",
    role: "Outreach Lead",
    bio: "Builds relationships with schools and communities to bring Byte-Sized Python to new students, connecting our mission with the people it serves.",
    photo: './vishal.png' as string | null,
    accentColor: "from-primary/20 to-secondary/20",
  },
];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
};

export const AboutTeamSection = () => {
  return (
    <section className="relative py-20 lg:py-28 overflow-hidden isolate">
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_80%_20%,hsl(48_100%_61%/.14),transparent_45%),radial-gradient(circle_at_20%_30%,hsl(211_52%_44%/.14),transparent_50%)]" />

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="max-w-6xl mx-auto space-y-12"
        >
          <motion.div variants={item} className="text-center space-y-4">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-primary/80">
              Meet the Team
            </p>
            <h2 className="text-4xl sm:text-5xl font-light text-foreground">
              The people behind{" "}
              <span className="text-primary">Byte-Sized Python</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Our leadership team is made up of passionate students who believe every kid deserves a chance to learn how to code.
            </p>
          </motion.div>

          <motion.div variants={item} className="relative px-10 sm:px-12">
            <Carousel
              opts={{ align: "start", loop: true, slidesToScroll: "auto" }}
              className="w-full"
            >
              <CarouselContent className="-ml-4">
                {leaders.map((officer, index) => (
                  <CarouselItem
                    key={index}
                    className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3"
                  >
                    <div className="h-full bg-white/90 border border-border rounded-2xl shadow-card backdrop-blur-sm overflow-hidden flex flex-col">
                      {/* Photo */}
                      <div className={`relative h-52 bg-gradient-to-br ${officer.accentColor} flex items-center justify-center flex-shrink-0`}>
                        {officer.photo ? (
                          <img
                            src={officer.photo}
                            alt={officer.name}
                            width={700}
                            height={500}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="flex flex-col items-center gap-3">
                            <div className="w-20 h-20 rounded-full bg-white/60 border border-border/60 flex items-center justify-center shadow-inner">
                              <span className="text-3xl font-bold text-primary">
                                {officer.name.charAt(0)}
                              </span>
                            </div>
                            <p className="text-xs text-foreground/50">Photo coming soon</p>
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex flex-col gap-4 p-6 flex-1">
                        <div className="space-y-1">
                          <Badge
                            variant="secondary"
                            className="text-xs tracking-wide"
                          >
                            {officer.role}
                          </Badge>
                          <h3 className="text-lg font-semibold text-foreground mt-2">
                            {officer.name}
                          </h3>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                          {officer.bio}
                        </p>
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="flex" />
              <CarouselNext className="flex" />
            </Carousel>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
